import React, { useEffect } from "react";
import { Button, MenuItem, Select, Stack, TextField, Typography } from "@mui/material";
import { Plus } from "@phosphor-icons/react";
import { useFormikContext } from "formik";

import FieldGroup from "../../../../shared/FieldGroup";
import TaskStepRow from "../../elements/TaskStepRow";
import type {
  TargetFormValues,
  TaskAnalysisProcedure as TaskAnalysisProcedureValues,
} from "../../TargetBuilderPanel.types";
import {
  CHAINING_OPTIONS,
  STEP_SCORING_OPTIONS,
  isTaskAnalysisProcedure,
} from "../../TargetBuilderPanel.types";
import { getHiddenPromptHierarchyTaskAnalysisPatch } from "./promptHierarchyNormalization";
import PromptHierarchyEditor from "./PromptHierarchyEditor";
import TeachingProcedureCommonFields from "./TeachingProcedureCommonFields";

const MIN_STEPS = 2;

interface TaskAnalysisProcedureProps {
  /** When true, hide the Prompt Hierarchy editor (baseline, anchored-rating-scale, caregiver, or behavior-reduction). */
  hidePromptHierarchy?: boolean;
  /** When true, hide Teaching Opportunities per Session (caregiver goals use Practice Opportunities instead). */
  hideTeachingOpportunities?: boolean;
  /** When true, mark L3-specific fields as optional (caregiver mode). */
  fieldsOptional?: boolean;
}

/**
 * Teaching Procedure form when L3 = task-analysis.
 * Chained step list with drag-reorder controls (drag handle is presentational
 * in this scaffold — keyboard up/down buttons are the real reorder mechanism).
 */
const TaskAnalysisProcedure: React.FC<TaskAnalysisProcedureProps> = ({
  hidePromptHierarchy,
  hideTeachingOpportunities,
  fieldsOptional,
}) => {
  const { values, setFieldValue } = useFormikContext<TargetFormValues>();
  if (!isTaskAnalysisProcedure(values.teaching_procedure)) return null;

  const procedure: TaskAnalysisProcedureValues = values.teaching_procedure;

  const update = (partial: Partial<TaskAnalysisProcedureValues>) =>
    setFieldValue("teaching_procedure", { ...procedure, ...partial });

  useEffect(() => {
    if (!hidePromptHierarchy) return;
    const patch = getHiddenPromptHierarchyTaskAnalysisPatch(procedure);
    if (!patch) return;
    update(patch);
  }, [hidePromptHierarchy, procedure.step_scoring, procedure.prompt_hierarchy?.levels?.length]);

  const stepScoringOptions = hidePromptHierarchy
    ? STEP_SCORING_OPTIONS.filter((opt) => opt.value !== "prompt-hierarchy")
    : STEP_SCORING_OPTIONS;

  const updateStep = (index: number, value: string) => {
    const next = procedure.steps.slice();
    next[index] = value;
    update({ steps: next });
  };

  const moveStep = (index: number, dir: -1 | 1) => {
    const target = index + dir;
    if (target < 0 || target >= procedure.steps.length) return;
    const next = procedure.steps.slice();
    [next[index], next[target]] = [next[target], next[index]];
    update({ steps: next });
  };

  const deleteStep = (index: number) => {
    if (procedure.steps.length <= MIN_STEPS) return;
    update({ steps: procedure.steps.filter((_, i) => i !== index) });
  };

  const addStep = () => update({ steps: [...procedure.steps, ""] });

  return (
    <Stack gap={4}>
      <TeachingProcedureCommonFields />

      <FieldGroup
        label="Task Steps"
        description="Define the steps in the task chain (minimum 2 steps required)"
        required={!fieldsOptional}
      >
        <Stack gap={2}>
          {procedure.steps.map((step, idx) => (
            <TaskStepRow
              key={idx}
              index={idx}
              value={step}
              canMoveUp={idx > 0}
              canMoveDown={idx < procedure.steps.length - 1}
              canDelete={procedure.steps.length > MIN_STEPS}
              onChange={(v) => updateStep(idx, v)}
              onMoveUp={() => moveStep(idx, -1)}
              onMoveDown={() => moveStep(idx, 1)}
              onDelete={() => deleteStep(idx)}
            />
          ))}
        </Stack>
        <Button
          variant="outlined"
          color="inherit"
          fullWidth
          startIcon={<Plus size={16} />}
          onClick={addStep}
          sx={{ mt: 2 }}
        >
          Add Step
        </Button>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
          {procedure.steps.length} steps defined
        </Typography>
      </FieldGroup>

      <FieldGroup
        label="Chaining Method"
        description="How will the steps be taught?"
        required={!fieldsOptional}
      >
        <Select
          size="small"
          fullWidth
          value={procedure.chaining}
          onChange={(e) =>
            update({ chaining: e.target.value as TaskAnalysisProcedureValues["chaining"] })
          }
          aria-label="Chaining method"
          displayEmpty
          renderValue={(selected) =>
            selected ? (
              CHAINING_OPTIONS.find((o) => o.value === selected)?.label
            ) : (
              <Typography variant="body2" color="text.disabled">
                Select chaining method
              </Typography>
            )
          }
        >
          {CHAINING_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </FieldGroup>

      <FieldGroup
        label="Step Scoring"
        description="How will each step be scored?"
        required={!fieldsOptional}
      >
        <Select
          size="small"
          fullWidth
          value={procedure.step_scoring}
          onChange={(e) =>
            update({ step_scoring: e.target.value as TaskAnalysisProcedureValues["step_scoring"] })
          }
          aria-label="Step scoring method"
          displayEmpty
          renderValue={(selected) =>
            selected ? (
              stepScoringOptions.find((o) => o.value === selected)?.label
            ) : (
              <Typography variant="body2" color="text.disabled">
                Select scoring method
              </Typography>
            )
          }
        >
          {stepScoringOptions.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </FieldGroup>

      {!hidePromptHierarchy && procedure.step_scoring === "prompt-hierarchy" && (
        <PromptHierarchyEditor />
      )}

      {!hideTeachingOpportunities && (
        <FieldGroup
          label="Teaching Opportunities per Session"
          description="What is the minimum teaching opportunities you plan for each session?"
          required={!fieldsOptional}
        >
          <TextField
            size="small"
            type="number"
            value={procedure.teaching_opportunities_per_session ?? ""}
            onChange={(e) => update({ teaching_opportunities_per_session: e.target.value })}
            placeholder="e.g., 5"
            aria-label="Teaching opportunities per session"
            inputProps={{ min: 1 }}
            InputProps={{ sx: { width: 120 } }}
          />
        </FieldGroup>
      )}

      <FieldGroup
        label="Step Advancement Criteria"
        description="When is a step considered mastered?"
      >
        <TextField
          multiline
          minRows={3}
          fullWidth
          value={procedure.step_advancement_criteria}
          onChange={(e) => update({ step_advancement_criteria: e.target.value })}
          placeholder="e.g., 'Step is mastered after 3 consecutive independent completions across 2 sessions'"
          aria-label="Step advancement criteria"
        />
      </FieldGroup>
    </Stack>
  );
};

export default TaskAnalysisProcedure;
